import React from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import api, { NIA_LOGO_URL, GOOGLE_LOGIN_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { Loader2, Mail, ShieldCheck } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login, config } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [globalLogo, setGlobalLogo] = useState("");

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => {
      setGlobalLogo(data.global_logo_url || "");
    }).catch(() => {});
    const err = params.get("error");
    if (err) {
      // Show as long-lived toast so admin has time to read the friendly rejection
      toast.error(decodeURIComponent(err), { duration: 10000 });
    }
  }, []);

  async function handleDevLogin(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/dev-login", { email, name });
      login(data.token, data.user);
      toast.success("Bem-vindo, " + data.user.name);
      navigate(data.user.role === "super_admin" ? "/admin/sectors" : "/coordinator/documents");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no login");
    } finally {
      setLoading(false);
    }
  }

  function handleGoogleLogin() {
    // Redirect ABSOLUTO para o subdomínio da API (evita CORS/cookies cross-site).
    // Em produção resolve para "https://api.chatbot.nialab.com.br/api/auth/google/login".
    // Em dev, resolve para o REACT_APP_BACKEND_URL configurado no .env.
    window.location.href = GOOGLE_LOGIN_URL;
  }

  const displayLogo = globalLogo || NIA_LOGO_URL;

  return (
    <div className="flex h-screen w-full bg-white">
      {/* Left decorative panel */}
      <div
        className="hidden lg:flex w-1/2 relative bg-slate-900 items-center justify-center overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(135deg, #0f2f4d 0%, #1e5a8e 55%, #2d82c4 100%)",
        }}
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 30%, rgba(255,255,255,0.15) 0px, transparent 40%), radial-gradient(circle at 75% 70%, rgba(64,224,208,0.25) 0px, transparent 45%)",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative z-10 max-w-md px-10 text-white"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-cyan-200/90 mb-6">
            IFPR · Chatbot Institucional
          </p>
          <h1 className="font-[Outfit] text-4xl sm:text-5xl font-bold leading-tight tracking-tight mb-6">
            Atendimento inteligente,
            <br />
            setor por setor.
          </h1>
          <p className="text-base text-slate-200/85 leading-relaxed mb-8">
            Cada setor com sua própria base de conhecimento. Respostas rastreáveis,
            contexto isolado e fallback direto para o contato humano.
          </p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-5 h-5 text-cyan-300 mt-0.5" />
              <span className="text-slate-200/80">Autenticação via Google Workspace</span>
            </div>
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-5 h-5 text-cyan-300 mt-0.5" />
              <span className="text-slate-200/80">RAG por setor com filtro obrigatório</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Right form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12 lg:p-16">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="flex justify-center mb-10">
            <img
              src={displayLogo}
              alt="NIA"
              className="h-24 object-contain"
              data-testid="global-logo"
            />
          </div>

          <h2 className="font-[Outfit] text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 mb-2 text-center">
            Acesso administrativo
          </h2>
          <p className="text-sm text-slate-500 text-center mb-8">
            Entre com sua conta institucional para gerenciar setores, documentos e o chatbot.
          </p>

          <Card className="p-6 border-slate-200 shadow-sm">
            {config.google_enabled ? (
              <Button
                data-testid="google-login-btn"
                onClick={handleGoogleLogin}
                className="w-full bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 h-11"
                variant="outline"
              >
                <GoogleIcon className="w-5 h-5 mr-3" />
                Entrar com Google
              </Button>
            ) : (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTitle className="text-amber-900">Google OAuth não configurado</AlertTitle>
                <AlertDescription className="text-amber-800 text-xs">
                  Defina <code>GOOGLE_CLIENT_ID</code> e <code>GOOGLE_CLIENT_SECRET</code> no
                  arquivo <code>backend/.env</code> para habilitar o login com Google.
                </AlertDescription>
              </Alert>
            )}

            {config.dev_mode && (
              <>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Modo Dev
                    </span>
                  </div>
                </div>

                <form onSubmit={handleDevLogin} className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      E-mail institucional
                    </label>
                    <div className="relative mt-1">
                      <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        data-testid="dev-login-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="voce@ifpr.edu.br"
                        className="pl-9 h-11"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Nome (opcional)
                    </label>
                    <Input
                      data-testid="dev-login-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Fulano de Tal"
                      className="mt-1 h-11"
                    />
                  </div>
                  <Button
                    data-testid="dev-login-submit"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#1e5a8e] hover:bg-[#164570] text-white h-11"
                  >
                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Entrar (modo dev)
                  </Button>
                </form>
                <p className="text-[11px] text-slate-400 mt-3 text-center">
                  O modo dev deve ser desativado em produção (DEV_MODE_LOGIN=false).
                </p>
              </>
            )}
          </Card>

          <p className="text-center text-xs text-slate-400 mt-8">
            NIA · Núcleo de Inteligência Artificial do IFPR
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function GoogleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" {...props}>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}
